// TelcoTrack - Reset Password Component
// Form to set new password using token from email link

import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import {
  PASSWORD_POLICY_HINT,
  mapPasswordPolicyApiError,
  validatePasswordPolicy,
} from '../../../../core/utils/password-policy.util';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styles: []
})
export class ResetPasswordComponent implements OnInit {
  readonly passwordPolicyHint = PASSWORD_POLICY_HINT;

  resetForm: FormGroup;
  token: string | null = null;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService
  ) {
    this.resetForm = new FormGroup({
      password: new FormControl('', [Validators.required, this.passwordPolicyValidator]),
      confirmPassword: new FormControl('', [Validators.required])
    }, { validators: this.passwordMatchValidator });
  }

  private passwordPolicyValidator(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value : '';
    if (!value) return null;
    const result = validatePasswordPolicy(value);
    return result.isValid ? null : { passwordPolicy: result.errors[0] };
  }

  passwordMatchValidator(control: AbstractControl) {
    const form = control as FormGroup;
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ ...(confirmPassword.errors ?? {}), passwordMismatch: true });
      return { passwordMismatch: true };
    }
    if (confirmPassword?.errors?.['passwordMismatch']) {
      const { passwordMismatch: _removed, ...rest } = confirmPassword.errors;
      confirmPassword.setErrors(Object.keys(rest).length ? rest : null);
    }
    return null;
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/maps']);
      return;
    }
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || null;
      if (!this.token) {
        this.errorMessage = 'Chybí odkaz pro reset hesla. Požádejte znovu o reset hesla.';
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (!this.token || !this.resetForm.valid) {
      if (!this.token) {
        this.errorMessage = 'Chybí odkaz pro reset hesla. Požádejte znovu o reset hesla.';
      } else {
        this.markFormGroupTouched();
      }
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await firstValueFrom(this.authService.resetPassword(this.token, this.resetForm.value.password));
      this.successMessage = 'Heslo bylo úspěšně změněno. Za chvíli vás přesměrujeme na přihlášení.';
      this.resetForm.reset();
      setTimeout(() => this.router.navigate(['/auth/login']), 2000);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : '';
      this.errorMessage = msg
        ? mapPasswordPolicyApiError(msg)
        : 'Reset hesla se nezdařil. Zkuste to prosím znovu.';
    } finally {
      this.isLoading = false;
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.resetForm.controls).forEach(key => {
      this.resetForm.get(key)?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.resetForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return 'Toto pole je povinné';
      if (field.errors['passwordPolicy']) return String(field.errors['passwordPolicy']);
      if (field.errors['passwordMismatch']) return 'Hesla se neshodují';
    }
    return '';
  }
}
